<template>
    <v-container fluid class="pa-4 pa-md-6">
        <div class="d-flex align-center mb-4 ga-2">
            <v-btn icon="mdi-arrow-left" variant="text" @click="back"/>
            <div>
                <h1 class="text-h5 font-weight-bold">Submission history</h1>
                <div class="text-body-2 text-medium-emphasis">Compliance submissions sent to CARB</div>
            </div>
        </div>

        <v-card rounded="lg">
            <v-data-table :headers="headers" :items="rows" :loading="loading" :items-per-page="25"
                          hide-default-footer density="comfortable">
                <template #item.carb_status="{item}">
                    <v-chip size="small" :color="carbColor(item.carb_status)" variant="tonal">
                        {{ item.carb_status || 'pending' }}
                    </v-chip>
                </template>
                <template #item.collected_at="{item}">
                    {{ item.collected_at ? formatDateTime(item.collected_at) : '-' }}
                </template>
                <template #item.submitted_at="{item}">
                    {{ item.submitted_at ? formatDateTime(item.submitted_at) : 'Not submitted' }}
                </template>
                <template #item.transport_status="{item}">
                    <v-chip size="x-small" variant="tonal">{{ item.transport_status }}</v-chip>
                </template>
                <template #item.actions="{item}">
                    <v-btn size="small" variant="text" prepend-icon="mdi-receipt-text-outline"
                           @click="openReceipt(item)">Receipt</v-btn>
                </template>
                <template #no-data>
                    <div class="py-8 text-center text-medium-emphasis">No submissions yet.</div>
                </template>
            </v-data-table>

            <div v-if="lastPage > 1" class="d-flex justify-center py-3">
                <v-pagination v-model="page" :length="lastPage" :total-visible="7" @update:model-value="load"/>
            </div>
        </v-card>

        <v-dialog v-model="receiptOpen" max-width="520">
            <v-card v-if="selected" rounded="lg">
                <v-card-title class="d-flex align-center">
                    <span class="text-h6">Submission receipt</span>
                    <v-spacer/>
                    <v-btn icon="mdi-printer" variant="text" size="small" @click="printReceipt"/>
                    <v-btn icon="mdi-close" variant="text" size="small" @click="receiptOpen = false"/>
                </v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col v-for="field in receiptFields" :key="field.label" cols="12" sm="6">
                            <div class="text-caption text-medium-emphasis">{{ field.label }}</div>
                            <div class="text-body-2 font-weight-medium">{{ field.value }}</div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';
import {formatDateTime} from '@/composables/useTimezone';

const router = useRouter();
const {showSnackbar} = useSnackbarStore();

const loading = ref(true);
const rows = ref([]);
const page = ref(1);
const lastPage = ref(1);
const receiptOpen = ref(false);
const selected = ref(null);

const headers = [
    {title: 'Asset', key: 'asset_name'},
    {title: 'VIN', key: 'vin'},
    {title: 'Collected', key: 'collected_at'},
    {title: 'Submitted', key: 'submitted_at'},
    {title: 'CARB result', key: 'carb_status'},
    {title: 'Transport', key: 'transport_status', sortable: false},
    {title: '', key: 'actions', sortable: false},
];

const carbColor = (status) => {
    if (status === 'pass') return 'success';
    if (status === 'fail') return 'error';
    if (!status) return 'info';
    return 'warning';
};

const back = () => router.push({name: 'ctc-compliance'});

const load = () => {
    loading.value = true;
    send('get', 'ctc/submissions', {page: page.value})
        .then((response) => {
            const payload = response.data.data;
            rows.value = payload.data ?? [];
            lastPage.value = payload.meta?.last_page ?? 1;
        })
        .catch(() => showSnackbar('Submissions could not be loaded', 'error'))
        .finally(() => loading.value = false);
};

const receiptFields = computed(() => {
    const s = selected.value;
    if (!s) return [];
    return [
        {label: 'Asset', value: s.asset_name || ('Asset ' + s.asset_id)},
        {label: 'VIN', value: s.vin || 'unknown'},
        {label: 'Device serial', value: s.device_serial || '-'},
        {label: 'Record ID', value: s.record_id ?? '-'},
        {label: 'Collected', value: s.collected_at ? formatDateTime(s.collected_at) : '-'},
        {label: 'Submitted', value: s.submitted_at ? formatDateTime(s.submitted_at) : 'Not submitted'},
        {label: 'CARB result', value: s.carb_status || 'pending'},
        {label: 'Transport', value: s.transport_status || '-'},
        {label: 'Receipt hash', value: s.receipt_hash || '-'},
        {label: 'File size', value: s.byte_size ? `${s.byte_size} bytes` : '-'},
    ];
});

const openReceipt = (item) => {
    selected.value = item;
    receiptOpen.value = true;
};

const printReceipt = () => {
    const s = selected.value;
    if (!s) return;
    const rowsHtml = receiptFields.value
        .map((f) => `<tr><td class="k">${f.label}</td><td>${f.value}</td></tr>`)
        .join('');
    const win = window.open('', '_blank', 'width=600,height=700');
    if (!win) return;
    win.document.write(`
        <html><head><title>Compliance submission receipt</title>
        <style>
            body{font-family:Arial,Helvetica,sans-serif;padding:32px;color:#1a1a1a}
            h1{font-size:18px;margin:0 0 4px}
            .sub{color:#666;font-size:12px;margin-bottom:20px}
            table{border-collapse:collapse;width:100%}
            td{padding:8px 6px;border-bottom:1px solid #eee;font-size:13px}
            td.k{color:#666;width:40%}
        </style></head>
        <body>
            <h1>Clean Truck Check submission receipt</h1>
            <div class="sub">SimpliGPS compliance record</div>
            <table>${rowsHtml}</table>
        </body></html>`);
    win.document.close();
    win.focus();
    win.print();
};

onMounted(load);
</script>
